import React, { useEffect, useState } from "react";
import { Typography, IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Spinner from "../Common/Spinner";
import { InventoryApiService } from "../../Service/InventoryApiService";
import { CommonService } from "../../Service/CommonService";
import { Service } from "../../Service/ApiService";
import { useHistory, useParams } from "react-router";
import InputMask from "react-input-mask";
import Select from "react-select";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';

const AddContract = () => {
  const [formData, setFormData] = useState({
    id: "",
    companyId: "",
    contractId: "",
    contractType: "",
    startDate: "",
    endDate: "",
    termMonths: "",
    initialQuantity: "",
    typeOfContainer: "",
    locationWarehouseId: "",
    contractPricingId: "",
    earlyTerminationPercentage: "",
    insuranceAnnuallyPricePerHundred: "",
    billingStartDate: "",
    billingCompanyName: "",
    billingContactName: "",
    billingContactPhone: "",
    billingContactEmail: "",
    billingAddress1: "",
    billingAddress2: "",
    billingAddress3: "",
    preferredPaymentMethod: "",
    description: "",
    additionalNotes: "",
    isEarlyTermination: false,
    insuranceWithWdts: false,
    isSubjectToPriceChange: false,
  });
  const [originalData, setOriginalData] = useState(null);
  const [errors, setErrors] = useState<any>({});
  const [isLoading, setIsLoading] = useState(false);
  const [companiesList, setCompaniesList] = useState([]);
  const [existingWarehouse, setExistingWarehouse] = useState([]);
  const [exitingServicePrice, setExistingServicePrice] = useState([]);
  const history = useHistory();
  const { id } = useParams<{ id: string }>();

  const fetchExistingCompanies = async () => {
    try {
      const response = await Service.fetchCompaniesList();
      setCompaniesList(response?.data);
    } catch (error) {
      console.error("Error fetching companies:", error);
    }
  };

  const fetchExistingWarehouse = async () => {
    try {
      const response = await InventoryApiService.fetchExistingWarehouse();
      setExistingWarehouse(response?.data);
    } catch (error) {
      console.error("Error fetching warehouse details:", error);
    }
  };

  const fetchExistingServicePricing = async () => {
    try {
      const response = await InventoryApiService.fetchExistingServicePricing();
      setExistingServicePrice(response?.data);
    } catch (error) {
      console.error("Error fetching service pricing details:", error);
    }
  };

  const fetchContractDetails = async () => {
    try {
      const response = await InventoryApiService.getContractById(id);
      setFormData(response.data);
      setOriginalData(response.data); // save original
    } catch (error) {
      console.log("Error occurred while api calling " + error)
    }
  }

  useEffect(() => {
    fetchExistingCompanies();
    fetchExistingServicePricing();
    fetchExistingWarehouse();

    if(id) {
      fetchContractDetails();
    }
  }, []);

  const getFormattedDate = () => {
    const today = new Date();
    const yy = today.getFullYear().toString().slice(-2);
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return `${yy}${mm}${dd}`;
  };

  const handleChange = async (e, isSelect = false, selectName = "") => {

     if (isSelect) {
      const { value } = e;

      // Remove error for this select field
      setErrors((prev) => ({
        ...prev,
        [selectName]: "",
      }));

      let updatedFormData = {
        ...formData,
        [selectName]: value,
      };

      setFormData(updatedFormData);
      return;
    }

    const { name, type, checked, value } = e.target;

    // Remove error for this field when user starts typing/changing
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));

    let updatedFormData = {
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    };

    // Auto-generate contractId when contractType changes
    // Auto-generate contractId when contractType changes
  if (name === "contractType") {
    const date = getFormattedDate();

    try {
      const response = await InventoryApiService.getNextContractId(value, date);
      updatedFormData.contractId = response.data;
    } catch (error) {
      console.error("Error generating contract ID:", error);

      // fallback if API fails
      const today = new Date();
      const yy = String(today.getFullYear()).slice(2);
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      updatedFormData.contractId = `${value}${yy}${mm}${dd}A`;
    }

    // Clear error for contractId field
    setErrors((prev) => ({ ...prev, contractId: "" }));
  }

    // Auto-calculate termMonths when startDate or endDate changes
    if (name === "startDate" || name === "endDate") {
      const start = name === "startDate" ? value : updatedFormData.startDate;
      const end = name === "endDate" ? value : updatedFormData.endDate;

      if (start && end) {
        const startDate = new Date(start);
        const endDate = new Date(end);

        if (endDate >= startDate) {
          let years = endDate.getFullYear() - startDate.getFullYear();
          let months = endDate.getMonth() - startDate.getMonth();
          let totalMonths = years * 12 + months;

          // Adjust if the end day is before the start day
          if (endDate.getDate() < startDate.getDate()) {
            totalMonths -= 1;
          }

          // Term should be minimum 1 even if same month
          updatedFormData.termMonths = (totalMonths + 1).toString(); // +1 because term starts from 1
        } else {
          updatedFormData.termMonths = ''; // or set error message here if needed
        }
      }
    }

    setFormData(updatedFormData);
  };


  const handleBlur = (e) => {
    const { name, value } = e.target;
    validateField(name, value);
  };

  const formatOptionLabel = ({ value, label }) => {
    if (value === "view_pricing") {
      return (
        <div style={{ display: "flex", alignItems: "center" }}>
          <RemoveRedEyeOutlinedIcon style={{ marginRight: "8px" }} />
          {label}
        </div>
      );
    }
    return label;
  };

  const validateField = (name, value) => {
    let newErrors = { ...errors };

    switch (name) {
      case "date":
        if (!value) {
          newErrors.date = "Date is required";
        } else {
          delete newErrors.date;
        }
        break;
      case "putAway":
        if (!value) {
          newErrors.putAway = "Put Away price is required";
        } else if (isNaN(parseFloat(value))) {
          newErrors.putAway = "Please enter a valid price";
        } else {
          delete newErrors.putAway;
        }
        break;
      default:
        // For other price fields, remove error if they have valid numeric value
        if (value && !isNaN(parseFloat(value))) {
          delete newErrors[name];
        }
        break;
    }

    setErrors(newErrors);
  };

  const validateContractDetails = () => {
    let isValid = true;
    let newErrors = { ...errors };

    // Required field validations
    if (!formData.companyId.trim()) {
      newErrors.companyId = "Company is required";
      isValid = false;
    }

    if (!formData.contractType.trim()) {
      newErrors.contractType = "Contract type is required";
      isValid = false;
    }

    if (!formData.startDate.trim()) {
      newErrors.startDate = "Start date is required";
      isValid = false;
    }

    if (!formData.endDate.trim()) {
      newErrors.endDate = "End date is required";
      isValid = false;
    }

    if (!formData.initialQuantity) {
      newErrors.initialQuantity = "Initial quantity is required";
      isValid = false;
    }

    if (!formData.typeOfContainer.trim()) {
      newErrors.typeOfContainer = "Container type is required";
      isValid = false;
    }

    if (!formData.locationWarehouseId.trim()) {
      newErrors.locationWarehouseId = "Warehouse is required";
      isValid = false;
    }

    if (!formData.contractPricingId.trim()) {
      newErrors.contractPricingId = "Contract pricing is required";
      isValid = false;
    }

    if (!formData.insuranceAnnuallyPricePerHundred) {
      newErrors.insuranceAnnuallyPricePerHundred = "Insurance annual price is required";
      isValid = false;
    }

    if (!formData.billingStartDate.trim()) {
      newErrors.billingStartDate = "Billing start date is required";
      isValid = false;
    }

    if (!formData.billingCompanyName.trim()) {
      newErrors.billingCompanyName = "Billing company name is required";
      isValid = false;
    }

    if (!formData.billingContactName.trim()) {
      newErrors.billingContactName = "Billing contact name is required";
      isValid = false;
    }

    if (!formData.billingContactPhone.trim()) {
      newErrors.billingContactPhone = "Billing contact phone is required";
      isValid = false;
    }

    if (!formData.billingContactEmail.trim()) {
      newErrors.billingContactEmail = "Billing contact email is required";
      isValid = false;
    }

    if (!formData.billingAddress1.trim()) {
      newErrors.billingAddress1 = "Billing address 1 is required";
      isValid = false;
    }

    if (!formData.billingAddress2.trim()) {
      newErrors.billingAddress2 = "Billing address 2 is required";
      isValid = false;
    }

    if (!formData.billingAddress3.trim()) {
      newErrors.billingAddress3 = "Billing address 3 is required";
      isValid = false;
    }

    // Date validations
    if (formData.startDate && formData.endDate) {
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);
      
      if (startDate >= endDate) {
        newErrors.endDate = "End date must be after start date";
        isValid = false;
      }
    }

    if (formData.startDate && formData.billingStartDate) {
      const startDate = new Date(formData.startDate);
      const billingStartDate = new Date(formData.billingStartDate);
      
      if (billingStartDate < startDate) {
        newErrors.billingStartDate = "Billing start date cannot be before contract start date";
        isValid = false;
      }
    }

    // Numeric validations
    if (formData.termMonths && (isNaN(Number(formData.termMonths)) || Number(formData.termMonths) <= 0)) {
      newErrors.termMonths = "Term months must be a positive number";
      isValid = false;
    }

    if (formData.initialQuantity && (isNaN(Number(formData.initialQuantity)) || Number(formData.initialQuantity) <= 0)) {
      newErrors.initialQuantity = "Initial quantity must be a positive number";
      isValid = false;
    }

    if (formData.insuranceAnnuallyPricePerHundred && (isNaN(Number(formData.insuranceAnnuallyPricePerHundred)) || Number(formData.insuranceAnnuallyPricePerHundred) < 0)) {
      newErrors.insuranceAnnuallyPricePerHundred = "Insurance annual price must be a valid number";
      isValid = false;
    }

    // Email validation
    if (formData.billingContactEmail) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.billingContactEmail)) {
        newErrors.billingContactEmail = "Please enter a valid email address";
        isValid = false;
      }
    }

    // Phone validation (basic)
    if (formData.billingContactPhone) {
      const phoneRegex = /^[\d\s\-\+\(\)]+$/;
      if (!phoneRegex.test(formData.billingContactPhone) || formData.billingContactPhone.length < 10) {
        newErrors.billingContactPhone = "Please enter a valid phone number";
        isValid = false;
      }
    }

    // Set errors
    setErrors(newErrors);
    
    return isValid;
  };

   // Map options for react-select
  const pricingOptions = exitingServicePrice.map((servicePrice) => ({
    value: servicePrice.id,
    label: `${servicePrice.type} (${new Date(servicePrice.date).toLocaleDateString()})`,
  }));

  // Add divider & "View Pricing" option
  const options = [
    ...pricingOptions,
    { value: "view_pricing", label: "View Pricing",customClass: 'view-pricing-link' },
  ];

  const handleContractSubmit = async (event: any) => {
    event.preventDefault();

    //check validation
    if(! await validateContractDetails()) {
      return;
    }

    //check input data update or not
    if (id && JSON.stringify(formData) === JSON.stringify(originalData)) {
      CommonService.Toast.fire({
        title: "No changes detected. Nothing to update.",
        icon: "info",
      });
      return;
    }

    try {
      setIsLoading(true);
      const response = await InventoryApiService.saveCompanyContract(formData);
      CommonService.Toast.fire({
        title: id ? "Contract Updated Successfully" : "Contract Submitted Successfully",
        icon: "success",
      });
      //redirect to the listing page
      history.push("/master/view-contract");
    } catch (error) {
      console.log("Error occurred while saving the contract");
    } finally {
      setIsLoading(false);
    }
  };

  const customStyles = {
      option: (provided, state) => ({
        ...provided,
        color: state.data.value === 'view_pricing' ? '#496673' : provided.color,
        borderTop: state.data.value === 'view_pricing' ? '1px solid #ccc' : 'none',
        paddingTop: state.data.value === 'view_pricing' ? '10px' : provided.paddingTop,
        paddingInline: state.data.value === 'view_pricing' ? '5px' :'5px',
        cursor: state.data.value === 'view_pricing' ? 'pointer' :'default',
      }),
  };

  const routeBack = () => {
    // Handle back navigation
    history.goBack();
  };

  const companyOptions = companiesList.map((company) => ({
    value: company.id,
    label: company.companyName,
  }));

  return (
    <>
      <div className="add-company-user-wrapper">
        <div className="d-flex align-items-center mb-3 px-3">
          <div className="back__button" onClick={routeBack}>
            <IconButton
              aria-label="ArrowBackIcon"
              sx={{ backgroundColor: "#FFF" }}
            >
              <ArrowBackIcon sx={{ color: "#1C1B1F" }} />
            </IconButton>
          </div>
          <div className="title">
            <Typography
              variant="h1"
              component="h2"
              sx={{ ml: 2, fontWeight: 600, fontSize: "25px" }}
            >
              {id ? 'Amendment Contract' : 'Add New Contract'}
            </Typography>
          </div>
        </div>

        <div className="add-company-user-form-wrapper p-0">
          <form className="form__inner row d-flex mx-auto py-5">
            {/* Type and Date Row */}

            <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3 form-group-wrapper">
              <label htmlFor="companyId" className="form-label">
                Company <span className="star-required text-danger">*</span>
              </label>
             <Select
                id="companyId"
                name="companyId"
                className={`select-company-dropdown form-control add-contract-input w-100 multi-select-dropdown   ${
                  errors.companyId ? "is_invalid" : ""
                }`}
                options={companyOptions}
                placeholder="Select Company"
                value={companyOptions.find(
                  (option) => option.value === formData.companyId
                )}
                onChange={(selectedOption) =>
                  handleChange({
                    target: { name: "companyId", value: selectedOption?.value },
                  })
                }
                isDisabled={id ? true : false}
                components={{
                  DropdownIndicator: () => <ExpandMoreRoundedIcon />,
                  IndicatorSeparator: () => null,
                }}
                isSearchable
              />
              {errors.companyId && (
                <div className="error-message">{errors.companyId}</div>
              )}
            </div>

            <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3 form-group-wrapper">
              <label htmlFor="contractType" className="form-label">
                Contract Type{" "}
                <span className="star-required text-danger">*</span>
              </label>
              <select
                id="contractType"
                name="contractType"
                value={formData.contractType}
                onChange={handleChange}
                className={`form-control add-contract-input ${
                  errors.contractType ? "is_invalid" : ""
                }`}
              >
                <option value="">Select contract type</option>
                <option value="NFA">NFA</option>
                <option value="SA">SA</option>
                <option value="WSA">WSA</option>
                <option value="3PWSA">3PWSA</option>
                <option value="PGSA">PGSA</option>
                <option value="BSA">BSA</option>
                <option value="RFA">RFA</option>
                <option value="NDA">NDA</option>
                <option value="BPA">BPA</option>
                <option value="PWSA">PWSA</option>
              </select>
              {errors.contractType && (
                <div className="error-message">{errors.contractType}</div>
              )}
            </div>

            <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3 form-group-wrapper">
              <label htmlFor="contractId" className="form-label">
                Contract ID <span className="star-required text-danger">*</span>
              </label>
              <input
                type="text"
                id="contractId"
                name="contractId"
                placeholder="Contract ID"
                value={formData.contractId}
                readOnly
                disabled
                className={`form-control add-contract-input ${
                  errors.contractId ? "is_invalid" : ""
                }`}
              />
              {errors.contractId && (
                <div className="error-message">{errors.contractId}</div>
              )}
            </div>

            <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3 form-group-wrapper">
              <label htmlFor="startDate" className="form-label">
                Start Date <span className="star-required text-danger">*</span>
              </label>
              <input
                type="date"
                id="startDate"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                onBlur={handleBlur}
                onKeyDown={(e) => e.preventDefault()} // disables manual typing
                className={`form-control add-contract-input ${
                  errors.startDate ? "is_invalid" : ""
                }`}
              />
              {errors.startDate && (
                <div className="error-message">{errors.startDate}</div>
              )}
            </div>

            <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3 form-group-wrapper">
              <label htmlFor="date" className="form-label">
                End Date <span className="star-required text-danger">*</span>
              </label>
              <input
                type="date"
                id="endDate"
                name="endDate"
                value={formData.endDate}
                onKeyDown={(e) => e.preventDefault()} // disables manual typing
                onChange={handleChange}
                onBlur={handleBlur}
                className={`form-control add-contract-input ${errors.endDate ? "is_invalid" : ""}`}
              />
              {errors.endDate && (
                <div className="error-message">{errors.endDate}</div>
              )}
            </div>

            {/* Storage Services */}
            <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3 form-group-wrapper">
              <label htmlFor="termMonths" className="form-label">
                Term <span className="star-required text-danger">*</span>
              </label>
              <input
                type="text"
                id="termMonths"
                name="termMonths"
                placeholder="Term (Months)"
                value={formData.termMonths}
                readOnly
                disabled
                className={`form-control add-contract-input ${
                  errors.termMonths ? "is_invalid" : ""
                }`}
              />
              {errors.termMonths && (
                <div className="error-message">{errors.termMonths}</div>
              )}
            </div>

            <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3 form-group-wrapper">
              <label htmlFor="typeOfContainer" className="form-label">
                Container Type{" "}
                <span className="star-required text-danger">*</span>
              </label>
              <select
                id="typeOfContainer"
                name="typeOfContainer"
                value={formData.typeOfContainer}
                onChange={handleChange}
                className={`form-control add-contract-input ${
                  errors.typeOfContainer ? "is_invalid" : ""
                }`}
              >
                <option value ="">Select container type</option>
                <option value="Barrel">Barrel</option>
                <option value="Tote">Tote</option>
                <option value="Pallet">Pallet</option>
                <option value="Drum">Drum</option>
              </select>
              {errors.typeOfContainer && (
                <div className="error-message">{errors.typeOfContainer}</div>
              )}
            </div>

            <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3 form-group-wrapper">
              <label htmlFor="initialQuantity" className="form-label">
                Initial Quantity{" "}
                <span className="star-required text-danger">*</span>
              </label>
              <input  
                type="text"
                id="initialQuantity"
                name="initialQuantity"
                placeholder="Enter Initial Quantity"
                value={formData.initialQuantity}
                onChange={(e) => {
                  // Allow change only if the input contains at least one digit
                  if (e.target.value === '' || /^\d+$/.test(e.target.value)) {
                    handleChange(e);
                  }
                }}
                onBlur={handleBlur}
                className={`form-control add-contract-input ${
                  errors.initialQuantity ? "is_invalid" : ""
                }`}
              />
              {errors.initialQuantity && (
                <div className="error-message">{errors.initialQuantity}</div>
              )}
            </div>

            <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3 form-group-wrapper">
              <label htmlFor="locationWarehouseId" className="form-label">
                Location Warehouse{" "}
                <span className="star-required text-danger">*</span>
              </label>
              <select
                id="locationWarehouseId"
                name="locationWarehouseId"
                value={formData.locationWarehouseId}
                onChange={handleChange}
                className={`form-control add-contract-input ${
                  errors.locationWarehouseId ? "is_invalid" : ""
                }`}
              >
                <option value="">Select Warehouse</option>
                {existingWarehouse.map((warehouse) => (
                  <option key={warehouse.name} value={warehouse.id}>
                    {warehouse.name}
                  </option>
                ))}
              </select>
              {errors.locationWarehouseId && (
                <div className="error-message">
                  {errors.locationWarehouseId}
                </div>
              )}
            </div>

            <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3 form-group-wrapper">
              <label htmlFor="contractPricingId" className="form-label">
                Contract Pricing{" "}
                <span className="star-required text-danger">*</span>
              </label>
              <Select
                id="contractPricingId"
                name="contractPricingId"
                options={options}
                styles={customStyles}
                value={options.find(opt => opt.value === formData.contractPricingId)}
                onChange={(selectedOption) => {
                  if (selectedOption.value === "view_pricing") {
                    history.push("/master/contract-pricing")
                  } else {
                    handleChange(selectedOption, true, "contractPricingId");
                  }
                }}
                placeholder= "Select Pricing"
                className={`select-company-dropdown form-control add-contract-input w-100 multi-select-dropdown ${
                  errors.contractPricingId ? "is_invalid" : ""
                }`}
                components={{
                  DropdownIndicator: () => <ExpandMoreRoundedIcon />,
                  IndicatorSeparator: () => null,
                }}
                formatOptionLabel={formatOptionLabel}
              />
              {errors.contractPricingId && (
                <div className="error-message">{errors.contractPricingId}</div>
              )}
            </div>

            <div className="col-12 col-sm-6 col-md-4 col-lg-3">
              <div className="form-check d-flex align-items-start mt-4">
                <input
                  className="form-check-input contact-form-check"
                  type="checkbox"
                  id="isEarlyTermination"
                  name="isEarlyTermination"
                  checked={formData.isEarlyTermination}
                  onChange={handleChange}
                />
                <label
                  className="form-label"
                  htmlFor="isEarlyTermination"
                >
                  Early Termination Allowed?
                </label>
              </div>
            </div>

            <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3 form-group-wrapper">
              <label htmlFor="earlyTerminationPercentage" className="form-label">
                Early Termination Penalty (%)
              </label>
              <input
                type="text"
                id="earlyTerminationPercentage"
                name="earlyTerminationPercentage"
                placeholder="Enter Early Termination Penalty (%)"
                value={formData.earlyTerminationPercentage}
                onChange={(e) => {
                  // Allow change only if the input contains at least one digit
                  if (e.target.value === '' || /^\d+$/.test(e.target.value)) {
                    handleChange(e);
                  }
                }}
                onBlur={handleBlur}
                className="form-control add-contract-input"
              />
            </div>

            <div className="col-12 col-sm-6 col-md-4 col-lg-3">
              <div className="form-check d-flex align-items-start mt-4">
                <input
                  className="form-check-input contact-form-check "
                  type="checkbox"
                  id="insuranceWithWdts"
                  name="insuranceWithWdts"
                  checked={formData.insuranceWithWdts}
                  onChange={handleChange}
                />
                <label
                  className="form-label"
                  htmlFor="insuranceWithWdts"
                >
                  Insurance Included with WDTS?
                </label>
              </div>
            </div>

            <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3 form-group-wrapper">
              <label htmlFor="insuranceAnnuallyPricePerHundred" className="form-label">
                Insurance Annual Price (Per $100){" "}
                <span className="star-required text-danger">*</span>
              </label>
              <input
                type="text"
                id="insuranceAnnuallyPricePerHundred"
                name="insuranceAnnuallyPricePerHundred"
                placeholder="Enter Price (e.g 0.70 for 0.70)"
                value={formData.insuranceAnnuallyPricePerHundred}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow empty or valid decimal number (digits + one dot)
                  if (value === '' || /^\d*\.?\d*$/.test(value)) {
                    handleChange(e);
                  }
                }}
                onBlur={handleBlur}
                className={`form-control add-contract-input ${
                  errors.insuranceAnnuallyPricePerHundred ? "is_invalid" : ""
                }`}
              />
              {errors.insuranceAnnuallyPricePerHundred && (
                <div className="error-message">
                  {errors.insuranceAnnuallyPricePerHundred}
                </div>
              )}
            </div>

            <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3 form-group-wrapper">
              <label htmlFor="date" className="form-label">
                Billing Start Date{" "}
                <span className="star-required text-danger">*</span>
              </label>
              <input
                type="date"
                id="billingStartDate"
                name="billingStartDate"
                value={formData.billingStartDate}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`form-control add-contract-input ${
                  errors.billingStartDate ? "is_invalid" : ""
                }`}
              />
              {errors.billingStartDate && (
                <div className="error-message">{errors.billingStartDate}</div>
              )}
            </div>

            <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3 form-group-wrapper">
              <label htmlFor="billingStartDate" className="form-label">
                Billing Company Name{" "}
                <span className="star-required text-danger">*</span>
              </label>
              <input
                type="text"
                id="billingCompanyName"
                name="billingCompanyName"
                placeholder="Enter Billing Company Name"
                value={formData.billingCompanyName}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`form-control add-contract-input ${
                  errors.billingCompanyName ? "is_invalid" : ""
                }`}
              />
              {errors.billingCompanyName && (
                <div className="error-message">{errors.billingCompanyName}</div>
              )}
            </div>

            <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3 form-group-wrapper">
              <label htmlFor="barrelEmptying" className="form-label">
                Billing Contact Name{" "}
                <span className="star-required text-danger">*</span>
              </label>
              <input
                type="text"
                id="billingContactName"
                name="billingContactName"
                placeholder="Enter Billing Contact Name"
                value={formData.billingContactName}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`form-control add-contract-input ${
                  errors.billingContactName ? "is_invalid" : ""
                }`}
              />
              {errors.billingContactName && (
                <div className="error-message">{errors.billingContactName}</div>
              )}
            </div>

            <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3 form-group-wrapper">
              <label htmlFor="billingContactPhone" className="form-label">
                Billing Contact Phone{" "}
                <span className="star-required text-danger">*</span>
              </label>
              <InputMask
                  mask="(999) 999-9999"
                  value={formData.billingContactPhone}
                  onChange={handleChange}
                >
                    {(inputProps) => (
                        <input
                          type="text"
                          id="billingContactPhone"
                          name="billingContactPhone"
                          placeholder="Enter Billing Contact Phone"
                          value={formData.billingContactPhone}
                          onChange={handleChange}
                          className={`form-control add-contract-input ${
                            errors.billingContactPhone ? "is_invalid" : ""
                          }`}
                          {...inputProps}
                        />
                    )}
              </InputMask>
              {errors.billingContactPhone && (
                <div className="error-message">
                  {errors.billingContactPhone}
                </div>
              )}
            </div>

            <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3 form-group-wrapper">
              <label htmlFor="billingContactEmail" className="form-label">
                Billing Contact Email{" "}
                <span className="star-required text-danger">*</span>
              </label>
              <input
                type="text"
                id="billingContactEmail"
                name="billingContactEmail"
                placeholder="Enter Billing Contact Email"
                value={formData.billingContactEmail}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`form-control add-contract-input ${
                  errors.billingContactEmail ? "is_invalid" : ""
                }`}
              />
              {errors.billingContactEmail && (
                <div className="error-message">
                  {errors.billingContactEmail}
                </div>
              )}
            </div>

            <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3 form-group-wrapper">
              <label htmlFor="billingAddress1" className="form-label">
                Billing Address 1{" "}
                <span className="star-required text-danger">*</span>
              </label>
              <input
                type="text"
                id="billingAddress1"
                name="billingAddress1"
                placeholder="Enter Billing Address 1"
                value={formData.billingAddress1}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`form-control add-contract-input ${
                  errors.billingAddress1 ? "is_invalid" : ""
                }`}
              />
              {errors.billingAddress1 && (
                <div className="error-message">{errors.billingAddress1}</div>
              )}
            </div>

            <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3 form-group-wrapper">
              <label htmlFor="billingAddress2" className="form-label">
                Billing Address 2{" "}
                <span className="star-required text-danger">*</span>
              </label>
              <input
                type="text"
                id="billingAddress2"
                name="billingAddress2"
                placeholder="Enter Billing Address 2"
                value={formData.billingAddress2}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`form-control add-contract-input ${
                  errors.billingAddress2 ? "is_invalid" : ""
                }`}
              />
              {errors.billingAddress2 && (
                <div className="error-message">{errors.billingAddress2}</div>
              )}
            </div>

            <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3 form-group-wrapper">
              <label htmlFor="billingAddress3" className="form-label">
                Billing Address 3{" "}
                <span className="star-required text-danger">*</span>
              </label>
              <input
                type="text"
                id="billingAddress3"
                name="billingAddress3"
                placeholder="Enter Billing Address 3"
                value={formData.billingAddress3}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`form-control add-contract-input ${
                  errors.billingAddress3 ? "is_invalid" : ""
                }`}
              />
              {errors.billingAddress3 && (
                <div className="error-message">{errors.billingAddress3}</div>
              )}
            </div>
            <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3 form-group-wrapper">
              <label htmlFor="preferredPaymentMethod" className="form-label">
                Preferred Payment Method
              </label>
              <select
                id="preferredPaymentMethod"
                name="preferredPaymentMethod"
                value={formData.preferredPaymentMethod}
                onChange={handleChange}
                className="form-control add-contract-input"
              >
                <option value ="">Select preferred payment method</option>
                <option value="Cash">Cash</option>
                <option value="Card">Card</option>
                <option value="Net Banking">Net Banking</option>
              </select>
            </div>

            <div className="col-12 col-sm-6 col-md-4 col-lg-3 form-group-wrapper">
              <div className="form-check d-flex align-items-start mt-4">
                <input
                  className="form-check-input contact-form-check"
                  type="checkbox"
                  id="isSubjectToPriceChange"
                  name="isSubjectToPriceChange"
                  checked={formData.isSubjectToPriceChange}
                  onChange={handleChange}
                />
                <label
                  className="form-label"
                  htmlFor="isSubjectToPriceChange"
                >
                  Subject to Price Change?
                </label>
              </div>
            </div>

            <div className="col-12 col-sm-6 mb-3 form-group-wrapper">
              <label htmlFor="description" className="form-label">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                placeholder="Enter description"
                value={formData.description}
                onChange={handleChange}
                onBlur={handleBlur}
                className="form-control add-contract-input"
                rows={4}
                style={{ resize: "none" }}
              />
              {errors.description ? (
                <div className="error-message">{errors.description}</div>
              ): (
                <div className="character-length">Maximum length 5000 characters.</div>
              )}
            </div>

            <div className="col-12 col-sm-6 mb-3 form-group-wrapper">
              <label htmlFor="additionalNotes" className="form-label">
                Additional Notes
              </label>
              <textarea
                id="additionalNotes"
                name="additionalNotes"
                placeholder="Enter additional notes"
                value={formData.additionalNotes}
                onChange={handleChange}
                onBlur={handleBlur}
                className="form-control add-contract-input"
                rows={4}
                style={{ resize: "none" }}
              />
              {errors.additionalNotes ? (
                <div className="error-message">{errors.additionalNotes}</div>
              ): (
                <div className="character-length">Maximum length 5000 characters.</div>
              )}
            </div>

            {/* Submit Button */}
            <div className="col-12 d-flex justify-content-center mt-4 gap-4">
              <button
                type="reset"
                onClick={routeBack}
                className="btn secondary-btn register-btn mb-2"
                disabled={isLoading}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="btn primary-btn register-btn mb-2"
                onClick={handleContractSubmit}
                disabled={isLoading}
              >
                {isLoading && <Spinner />} {id ? "Update": "Submit"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default AddContract;
